import { cn } from "@/lib/cn";
import { shimmer, skeletonSurface } from "@/lib/shimmer";

/**
 * Circular gauge placeholder (burn / utilization). Uses a pulsing SVG
 * stroke-dasharray so it reads as "loading" rather than "empty".
 */
export function GaugeSkeleton({
  size = 220,
  className,
}: {
  size?: number;
  className?: string;
}) {
  const stroke = 14;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <div
      className={cn(
        "relative flex items-center justify-center rounded-[20px] border border-white/5 p-6",
        skeletonSurface,
        shimmer,
        className,
      )}
      style={{ minHeight: size + 48 }}
      aria-hidden="true"
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="block"
      >
        {/* Track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={stroke}
          fill="none"
        />
        {/* Pulsing arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(167,139,250,0.45)"
          strokeWidth={stroke}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={`${circumference * 0.35} ${circumference}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{
            animation: "skeleton-dash 1.8s ease-in-out infinite",
          }}
        />
      </svg>
      {/* Center value placeholder */}
      <div className="absolute flex flex-col items-center gap-2">
        <div className="h-7 w-20 rounded bg-white/[0.06]" />
        <div className="h-3 w-12 rounded bg-white/[0.04]" />
      </div>
    </div>
  );
}
